import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import Papa from 'papaparse';
import { Card, Row, Col, Space, Upload, Table, Button, Tag } from 'antd';
import { UploadOutlined, DownloadOutlined } from '@ant-design/icons';
import { PhoneNumberUtil, PhoneNumberFormat } from 'google-libphonenumber';
import usePrivateAxios from '../hooks/usePrivateAxios';
import StatusNotification from './StatusNotification';
import GlobalColors from '../assets/colors/GlobalColors';
import { HappyProvider } from '@ant-design/happy-work-theme';
import LoadingIcon from './LoadingIcon';
import isValidEmail from '../utils/isValidEmail';

const phoneUtil = PhoneNumberUtil.getInstance();

// Property type mapping
const PROPERTY_MAPPINGS = {
  name: 'str_first_name',
  surname: 'str_surname',
  country: 'str_country',
  country_code: 'str_country_code',
  gender: 'str_gender',
  age: 'int_age',
  age_group: 'str_age_group',
  race: 'str_race',
  province: 'str_province',
  region: 'str_region',
  town: 'str_town',
  marital_status: 'str_marital_status',
  is_married: 'bol_is_married',
  has_children: 'bol_has_children',
  number_of_children: 'int_number_of_children',
  children_ages: 'obj_children_ages',
  total_household_income: 'flt_total_household_income',
};

const convertValue = (value, prefix) => {
  switch (prefix) {
    case 'int_':
      return parseInt(value, 10);
    case 'flt_':
      return parseFloat(value);
    case 'bol_':
      return value.toLowerCase() === 'true';
    case 'obj_':
      try {
        return typeof value === 'string' ? JSON.parse(value) : value;
      } catch (e) {
        return value;
      }
    default:
      return String(value);
  }
};

const parseProperties = (propertiesString) => {
  if (!propertiesString) return [];

  const properties = [];
  const pairs = propertiesString.split(',').map((pair) => pair.trim());

  for (const pair of pairs) {
    const [key, value] = pair.split(':').map((s) => s.trim());
    if (!key || value === undefined) continue;

    // Get the mailjet property name
    const mailjetKey = PROPERTY_MAPPINGS[key];
    if (!mailjetKey) continue;

    // Get prefix to determine type (e.g., 'int_' from 'int_age')
    const prefix = mailjetKey.match(/^(str_|int_|bol_|flt_|obj_)/)?.[0] || 'str_';
    const convertedValue = convertValue(value, prefix);

    properties.push({
      [mailjetKey]: convertedValue,
    });
  }

  return properties;
};

const formatPhoneNumberForWhatsApp = (number, countrycode) => {
  try {
    const phoneNumber = phoneUtil.parseAndKeepRawInput(number, countrycode);
    const formattedNumber = phoneUtil.format(phoneNumber, PhoneNumberFormat.E164);
    return formattedNumber.slice(1);
  } catch (error) {
    console.error('Error formatting number:', error);
    return number;
  }
};

const HandleContacts = () => {
  const axiosPrivate = usePrivateAxios();
  const { organizationId } = useSelector((state) => state.user);
  const [accumulatedData, setAccumulatedData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [canUpload, setCanUpload] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const history = useHistory();

  const handleTagRemove = (record, tagToRemove) => {
    setAccumulatedData(prevData => 
      prevData.map(contact => {
        if (contact === record) {
          return {
            ...contact,
            tags: contact.tags
              .split(',')
              .map(tag => tag.trim())
              .filter(tag => tag !== tagToRemove)
              .join(', ')
          };
        }
        return contact;
      })
    );
  };

  const handleUpload = (uploadedFile) => {
    Papa.parse(uploadedFile, {
      header: true,
      skipEmptyLines: true,
      complete: (result) => {
        if (result.data && result.data.length > 0 && isValidCSV(result.data)) {
          const validData = result.data
            .filter(
              (row) =>
                row.name &&
                row.cellnumber &&
                row.countrycode &&
                row.name.length > 2 &&
                isValidEmail(row.email)
            )
            .map((row) => {
              const { countrycode, ...rest } = row;
              return {
                ...rest,
                countrycode: countrycode,
                email: row.email.toLowerCase(),
                cellnumber: formatPhoneNumberForWhatsApp(row.cellnumber, countrycode),
                properties: [...parseProperties(row.properties), { str_country_code: countrycode }],
              };
            });
          setAccumulatedData((prev) => [...prev, ...validData]);
          setCanUpload(true);
          StatusNotification('success', 'The data is all correct and ready for database upload');
        } else {
          StatusNotification(
            'error',
            'Invalid CSV format, compare with the sample csv file and try again'
          );
        }
      },
      error: () => {
        StatusNotification('error', 'Error parsing CSV.');
      },
    });
    return false;
  };

  const isValidCSV = (data) => {
    const sampleKeys = ['name', 'cellnumber', 'email', 'countrycode', 'tags', 'properties'];
    return sampleKeys.every((key) => Object.keys(data[0]).includes(key));
  };

  const uploadData = async () => {
    setIsLoading(true);
    try {
      await axiosPrivate.post(
        `api/contacts/upload/${organizationId}`,
        { contacts: accumulatedData },
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );
      setAccumulatedData([]);
      setIsLoading(false);
      StatusNotification('success', 'Contacts uploaded successfully.');
      history.push('/contacts');
    } catch (error) {
      setIsLoading(false);
      StatusNotification('error', error.response?.data?.message || 'Error uploading contacts.');
    }
  };

  const downloadSampleCSV = () => {
    const csv =
      'name,cellnumber,email,countrycode,tags,properties\nLesedi,+27801234567,lesedi@example.com,ZA,"contact,vip,new-client","age:25,city:Johannesburg"';
    const blob = new Blob([csv], { type: 'text/csv' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'sample.csv';
    link.click();
  };

  const resetAccumulatedData = () => {
    setAccumulatedData([]);
    setCanUpload(false);
  };

  const columns = [
    { title: 'Name', dataIndex: 'name', key: 'name' },
    { title: 'CellNumber', dataIndex: 'cellnumber', key: 'cellnumber' },
    { title: 'Email', dataIndex: 'email', key: 'email' },
    { title: 'CountryCode', dataIndex: 'countrycode', key: 'countrycode' },
    {
      title: 'Tags',
      dataIndex: 'tags',
      key: 'tags',
      render: (tags, record) => (
        <>
          {tags.split(',').map((tag) => {
            const trimmedTag = tag.trim();
            return trimmedTag ? (
              <Tag 
                color='blue' 
                key={trimmedTag}
                closable
                style={{ margin: '2px' }}
                onClose={(e) => {
                  e.preventDefault();
                  handleTagRemove(record, trimmedTag);
                }}
              >
                {trimmedTag}
              </Tag>
            ) : null;
          })}
        </>
      ),
    },
    {
      title: 'Properties',
      dataIndex: 'properties',
      key: 'properties',
      width: '300px',
      render: (properties, record) => (
        <div style={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>
          {Array.isArray(properties) &&
            properties.map((prop) => {
              if (!prop) return null;
              const entries = Object.entries(prop);
              if (entries.length === 0) return null;

              const [key, value] = entries[0];
              const displayKey = key.replace(/^(str_|int_|bol_|flt_|obj_)/, '');
              return (
                <Tag
                  closable
                  color='green'
                  key={key}
                  style={{ margin: '2px', display: 'inline-block' }}
                  onClose={() => handleTagClose(record, key)}
                >
                  {`${displayKey}: ${value}`}
                </Tag>
              );
            })}
        </div>
      ),
    },
  ];
  const handleTagClose = (record, keyToRemove) => {
    setAccumulatedData((prevData) =>
      prevData.map((contact) => {
        if (contact.cellnumber === record.cellnumber) {
          return {
            ...contact,
            properties: contact.properties.filter(
              (prop) => !Object.keys(prop).includes(keyToRemove)
            ),
          };
        }
        return contact;
      })
    );
  };

  return (
    <div style={{ marginBottom: 10, marginTop: 78 }}>
      <Row justify='space-between'>
        <Col push={1}>
          <Upload showUploadList={false} customRequest={({ file }) => handleUpload(file)}>
            <span className='icon' style={{ fontSize: 26 }}>
              <UploadOutlined style={{ color: GlobalColors.mainPurple, cursor: 'pointer' }} />
            </span>
          </Upload>
        </Col>
        <Col pull={1}>
          <Space align='center'>
            Download Sample CSV
            <span className='icon' style={{ fontSize: 26 }}>
              <DownloadOutlined
                onClick={downloadSampleCSV}
                style={{ color: GlobalColors.mainPurple }}
              />
            </span>
          </Space>
        </Col>
      </Row>
      <Row>
        <Col span={24}>
          <Card
            bordered={false}
            className='criclebox tablespace mb-24'
            title={
              accumulatedData.length === 0 ? (
                'No Contacts'
              ) : (
                <>
                  <Tag color={GlobalColors.lightPurple}>{accumulatedData.length}</Tag> Contacts
                </>
              )
            }
            style={{ padding: 12 }}
          >
            <Table
              dataSource={accumulatedData}
              columns={columns}
              rowKey='cellnumber'
              pagination={{
                size: 'small',
                position: ['bottomCenter'],
                current: currentPage,
                pageSize: pageSize,
                onChange: (page, pageSize) => {
                  setCurrentPage(page);
                  setPageSize(pageSize);
                },
              }}
            />
          </Card>
        </Col>
      </Row>
      <Row justify='space-between'>
        <Col span={12}>
          <HappyProvider>
            <Button type='primary' onClick={uploadData} disabled={!canUpload}>
              {isLoading ? <LoadingIcon /> : 'Upload'}
            </Button>
          </HappyProvider>
        </Col>
        <Col span={12} style={{ textAlign: 'end' }}>
          <HappyProvider>
            <Button type='primary' onClick={resetAccumulatedData} disabled={!canUpload}>
              {isLoading ? <LoadingIcon /> : 'Clear All'}
            </Button>
          </HappyProvider>
        </Col>
      </Row>
    </div>
  );
};

export default HandleContacts;
